import { useState, useEffect, useCallback } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Download, Trash2, Calendar as CalendarIcon, Clock, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import { toast as toastify } from "react-toastify";
import axios from "axios";
import CreateEventModal from "@/components/CreateEventModal";
import SyncCalendarModal from "@/components/SyncCalendarModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const localizer = momentLocalizer(moment);

// Configuración de moment en español
moment.locale('es');

export default function Calendar() {
  const { user, getPlanName, getPlanId } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  const planName = getPlanName();
  const planId = getPlanId();
  const isPro = planId === 'pro' || user?.subscription_plan === 'pro';

  // Check if user has Pro plan
  useEffect(() => {
    if (!user) return;
    
    if (!isPro) {
      toast.error("El calendario solo está disponible en el plan Pro");
      navigate('/pricing');
    }
  }, [user, navigate, isPro]);

  useEffect(() => {
    if (isPro) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/calendar/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform events for react-big-calendar
      const transformedEvents = response.data.map(event => ({
        ...event,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        title: event.title
      }));
      
      setEvents(transformedEvents);
      
      // Las notificaciones ahora se muestran en la página dedicada de Notificaciones
      // checkUpcomingEvents(transformedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar y notificar eventos próximos
  const checkUpcomingEvents = (eventsList) => {
    const now = new Date();
    const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const nextHour = new Date(now.getTime() + 60 * 60 * 1000);
    
    eventsList.forEach(event => {
      const eventStart = new Date(event.start);
      const timeDiff = eventStart - now;
      const minutesDiff = Math.floor(timeDiff / (1000 * 60));
      
      // Notificar si el evento está dentro de 1 hora
      if (timeDiff > 0 && eventStart <= nextHour) {
        toastify.info(`⏰ Recordatorio: "${event.title}" comienza en ${minutesDiff} minutos`, {
          position: "top-right",
          autoClose: 8000,
          closeOnClick: false,
        });
      }
      // Notificar si el evento es hoy
      else if (timeDiff > 0 && eventStart <= next24Hours) {
        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        toastify.info(`📅 Tienes el evento "${event.title}" en ${hours}h ${minutesDiff % 60}min`, {
          position: "top-right",
          autoClose: 6000,
        });
      }
    });
  };

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  const handleSelectSlot = useCallback(({ start, end }) => {
    setShowCreateModal(true);
  }, []);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿Estás seguro de eliminar este evento?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/calendar/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Evento eliminado");
      toastify.success("🗑️ Evento eliminado del calendario", {
        position: "top-right",
        autoClose: 3000,
      });
      
      fetchEvents();
      setSelectedEvent(null);
    } catch (error) {
      toast.error("Error al eliminar evento");
      toastify.error("Error al eliminar el evento", {
        position: "top-right",
      });
    }
  };

  const handleExportICS = async (eventId) => {
    // Open sync modal instead of direct download
    setShowSyncModal(true);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#f97316'; // orange-500
    
    if (event.event_type === 'reminder') {
      backgroundColor = '#3b82f6'; // blue-500
    }
    
    if (event.status === 'completed') {
      backgroundColor = '#10b981'; // green-500
    } else if (event.status === 'cancelled') {
      backgroundColor = '#6b7280'; // gray-500
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CalendarIcon className="w-12 h-12 animate-pulse text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Cargando calendario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
              <CalendarIcon className="text-orange-500" size={40} />
              Mi Calendario
            </h1>
            <p className="text-gray-600 mt-2">
              Agenda tus reuniones y recibe recordatorios automáticos
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowSyncModal(true)}
              variant="outline"
              className="gap-2"
            >
              <Download size={18} />
              Sincronizar con mi calendario
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 gap-2"
            >
              <Plus size={18} />
              Nuevo Evento
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar View */}
          <Card className="lg:col-span-2">
            <CardContent className="p-4">
              <div style={{ height: '600px' }}>
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  selectable
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  eventPropGetter={eventStyleGetter}
                  messages={{
                    next: "Siguiente",
                    previous: "Anterior",
                    today: "Hoy",
                    month: "Mes",
                    week: "Semana",
                    day: "Día",
                    agenda: "Agenda",
                    date: "Fecha",
                    time: "Hora",
                    event: "Evento",
                    noEventsInRange: "No hay eventos en este rango",
                    showMore: (total) => `+ Ver más (${total})`
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Event Details / Upcoming Events */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedEvent ? "Detalles del Evento" : "Próximos Eventos"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedEvent ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{selectedEvent.title}</h3>
                    {selectedEvent.description && (
                      <p className="text-sm text-gray-600 mb-3">{selectedEvent.description}</p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock size={16} />
                      <span>
                        {moment(selectedEvent.start).format('DD/MM/YYYY HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}
                      </span>
                    </div>

                    {selectedEvent.location && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin size={16} />
                        <span>{selectedEvent.location}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText size={16} />
                      <span className="capitalize">
                        {selectedEvent.event_type === 'meeting' ? 'Reunión' : 'Recordatorio'}
                      </span>
                    </div>
                  </div>

                  <div className="pt-4 space-y-2">
                    <Button
                      onClick={() => setShowSyncModal(true)}
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                    >
                      <Download size={16} />
                      Sincronizar con mi calendario
                    </Button>
                    <Button
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                      variant="outline"
                      size="sm"
                      className="w-full gap-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                      Eliminar Evento
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {events
                    .filter(e => new Date(e.start) > new Date())
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .slice(0, 5)
                    .map((event) => (
                      <div
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <h4 className="font-medium text-sm">{event.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {moment(event.start).format('DD/MM/YYYY HH:mm')}
                        </p>
                      </div>
                    ))}
                  
                  {events.filter(e => new Date(e.start) > new Date()).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">
                      No tienes eventos próximos
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onEventCreated={fetchEvents}
        initialDate={date}
      />

      <SyncCalendarModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        event={selectedEvent}
      />
    </div>
  );
}
